import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { colors, borderRadius, shadows } from '../styles/designTokens';
import { CustomerDetail } from '../models/CustomerModels';
import { CustomerService } from '../services/CustomerService';
import { MainContent, LoadingSpinner, ErrorMessage } from '../styles/DashboardStyles';
import CustomerDetailTabs from '../components/CustomerDetailTabs';
import PageHeader from '../components/ui/PageHeader';
import { formatDateTimeKST } from '../utils/dateUtils';

// 뒤로가기 버튼 스타일
const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.neutral[100]};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  padding: 8px 16px;
  font-size: 14px;
  color: ${colors.semantic.text.secondary};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 24px;

  &:hover {
    background: ${colors.neutral[200]};
    border-color: ${colors.secondary[400]};
  }
`;




// 정보 그리드 스타일
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;
`;

// 정보 카드 스타일
const InfoCard = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: 20px;
  border: 1px solid ${colors.neutral[200]};
  box-shadow: ${shadows.base};

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: ${colors.secondary[800]};
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid ${colors.neutral[100]};
  }
`;

// 정보 항목 스타일
const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${colors.neutral[50]};

  &:last-child {
    border-bottom: none;
  }

  label {
    font-size: 14px;
    font-weight: 500;
    color: ${colors.semantic.text.tertiary};
  }

  span {
    font-size: 14px;
    color: ${colors.secondary[800]};
    font-weight: 500;
  }
`;

const EditInput = styled.input`
  flex: 1;
  max-width: 55%;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  color: ${colors.secondary[800]};
  background: ${colors.semantic.background.primary};

  &:focus {
    outline: none;
    border-color: ${colors.primary[400]};
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }
`;

const ProfileSaveBar = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const SaveProfileButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.semantic.text.inverse};
  background: ${colors.primary[500]};
  border: none;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary[600]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProfileMessageText = styled.span<{ $error?: boolean }>`
  font-size: 13px;
  color: ${props => (props.$error ? colors.error[600] : colors.success[700])};
`;

// 통계 카드 그리드 스타일
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

// 통계 카드 스타일
const StatsCard = styled.div`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${borderRadius.xl};
  padding: 18px 16px;
  text-align: center;
  box-shadow: ${shadows.base};

  .title {
    font-size: 14px;
    color: ${colors.semantic.text.tertiary};
    margin-bottom: 12px;
    font-weight: 500;
  }

  .value {
    font-size: 22px;
    font-weight: 700;
    color: ${colors.secondary[800]};
    margin-bottom: 8px;
  }

  .change {
    font-size: 12px;
    font-weight: 500;
    color: ${colors.semantic.text.tertiary};
  }
`;

const SectionHeading = styled.h3`
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 700;
  color: ${colors.semantic.text.secondary};
`;



// 고객 상세 페이지 컴포넌트
const CustomerDetailPage: React.FC = () => {
  // URL 파라미터에서 userIdx 가져오기
  const { userIdx } = useParams<{ userIdx: string }>();
  const navigate = useNavigate();

  // 상태 관리
  const [customerDetail, setCustomerDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editBusinessLicense, setEditBusinessLicense] = useState('');
  const [editCorporateLicense, setEditCorporateLicense] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileFlash, setProfileFlash] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // 고객 상세 정보 로드
  const loadCustomerDetail = useCallback(async () => {
    if (!userIdx) {
      setError('고객 ID가 유효하지 않습니다.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 실제 API 호출 시도, 실패하면 목업 데이터 사용
      let detail: CustomerDetail;
      try {
        detail = await CustomerService.getCustomerDetail(parseInt(userIdx));
        console.log('실제 API 데이터 로드 성공:', detail);
      } catch (apiError) {
        setError('고객 상세 정보를 불러오는데 실패했습니다.');
        console.error('Customer detail loading error:', apiError);
        return;
      }

      setCustomerDetail(detail);
    } catch (err) {
      setError('고객 상세 정보를 불러오는데 실패했습니다.');
      console.error('Customer detail loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [userIdx]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadCustomerDetail();
  }, [loadCustomerDetail]);

  useEffect(() => {
    if (!customerDetail) return;
    setEditName(customerDetail.이름 ?? '');
    setEditBusinessLicense(customerDetail.사업자등록증 ?? '');
    setEditCorporateLicense(customerDetail.법인등록증 ?? '');
  }, [customerDetail]);

  const handleSaveProfile = async () => {
    if (!userIdx || !customerDetail) return;
    setProfileFlash(null);
    setProfileSaving(true);
    try {
      const updated = await CustomerService.patchCustomerProfile(parseInt(userIdx, 10), {
        이름: editName.trim(),
        사업자등록증: editBusinessLicense.trim() || null,
        법인등록증: editCorporateLicense.trim() || null,
      });
      setCustomerDetail(updated);
      setProfileFlash({ type: 'ok', text: '저장되었습니다.' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '저장에 실패했습니다.';
      setProfileFlash({ type: 'err', text: msg });
    } finally {
      setProfileSaving(false);
    }
  };

  // 뒤로가기 핸들러
  const handleGoBack = () => {
    navigate('/customers');
  };

  // 날짜 포맷팅 (KST 적용)
  const formatDateTime = (dateTimeString: string) => {
    return formatDateTimeKST(dateTimeString);
  };

  // 숫자 포맷팅
  const formatNumber = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  // 포인트 포맷팅
  const formatPoints = (points: number) => {
    return `${formatNumber(points)} P`;
  };

  // 금액 포맷팅
  const formatCurrency = (amount: number) => {
    return `${formatNumber(amount)}원`;
  };

  // 로딩 상태 처리
  if (loading) {
    return (
      <MainContent>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </MainContent>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <MainContent>
        <BackButton onClick={handleGoBack}>
          <ArrowLeft size={16} />
          고객 목록으로 돌아가기
        </BackButton>
        <ErrorMessage>{error}</ErrorMessage>
      </MainContent>
    );
  }

  // 데이터가 없는 경우 처리
  if (!customerDetail) {
    return (
      <MainContent>
        <BackButton onClick={handleGoBack}>
          <ArrowLeft size={16} />
          고객 목록으로 돌아가기
        </BackButton>
        <ErrorMessage>고객 정보를 찾을 수 없습니다.</ErrorMessage>
      </MainContent>
    );
  }

  return (
    <MainContent>
      <BackButton onClick={handleGoBack}>
        <ArrowLeft size={16} />
        고객 목록으로 돌아가기
      </BackButton>

      {/* 페이지 헤더 */}
      <PageHeader
        title="고객 상세 정보"
        description="사용자 상세 정보, 결제 내역, 키워드 정보, 메시지 로그를 확인하고 관리합니다."
      />

      <SectionHeading>핵심 지표</SectionHeading>
      <StatsGrid>
        <StatsCard>
          <div className="title">잔여 포인트</div>
          <div className="value">{formatPoints(customerDetail.잔여포인트)}</div>
          <div className="change">전일 사용: {formatPoints(customerDetail.전일사용포인트)}</div>
        </StatsCard>
        <StatsCard>
          <div className="title">총 결제 금액</div>
          <div className="value">{formatCurrency(customerDetail.총결제금액)}</div>
          <div className="change">한달: {formatCurrency(customerDetail.한달내결제금액)}</div>
        </StatsCard>
        <StatsCard>
          <div className="title">활성 키워드</div>
          <div className="value">{formatNumber(customerDetail.활성키워드수)}건</div>
          <div className="change">비활성: {formatNumber(customerDetail.비활성키워드수)}건</div>
        </StatsCard>
        <StatsCard>
          <div className="title">전일 발송 메시지</div>
          <div className="value">{formatNumber(customerDetail.전일메시지수)}건</div>
          <div className="change">메시지 발송</div>
        </StatsCard>
      </StatsGrid>

      <SectionHeading>기본/사업자 정보</SectionHeading>
      <InfoGrid>
        <InfoCard>
          <h3>기본 정보</h3>
          <InfoItem>
            <label>이름</label>
            <EditInput
              type="text"
              maxLength={20}
              value={editName}
              onChange={e => setEditName(e.target.value)}
              placeholder="이름 또는 회사명"
              aria-label="이름"
            />
          </InfoItem>
          <InfoItem>
            <label>사용자 유형</label>
            <span>{customerDetail.사용자유형}</span>
          </InfoItem>
          <InfoItem>
            <label>계정 ID</label>
            <span>{customerDetail.아이디}</span>
          </InfoItem>
          <InfoItem>
            <label>이메일</label>
            <span>{customerDetail.이메일}</span>
          </InfoItem>
          <InfoItem>
            <label>연락처</label>
            <span>{customerDetail.연락처}</span>
          </InfoItem>
          <InfoItem>
            <label>마케팅 동의</label>
            <span>{customerDetail.마케팅동의 ? '예' : '아니오'}</span>
          </InfoItem>
        </InfoCard>

        <InfoCard>
          <h3>사업자 정보</h3>
          <InfoItem>
            <label>사업자 등록증</label>
            <EditInput
              type="text"
              maxLength={12}
              value={editBusinessLicense}
              onChange={e => setEditBusinessLicense(e.target.value)}
              placeholder="없으면 비워 두세요"
              aria-label="사업자등록번호"
            />
          </InfoItem>
          <InfoItem>
            <label>법인 등록증</label>
            <EditInput
              type="text"
              maxLength={14}
              value={editCorporateLicense}
              onChange={e => setEditCorporateLicense(e.target.value)}
              placeholder="없으면 비워 두세요"
              aria-label="법인등록번호"
            />
          </InfoItem>
          <InfoItem>
            <label>가입일</label>
            <span>{formatDateTime(customerDetail.가입일)}</span>
          </InfoItem>
          <InfoItem>
            <label>최근 로그인</label>
            <span>{formatDateTime(customerDetail.최근로그인)}</span>
          </InfoItem>
        </InfoCard>
        <ProfileSaveBar>
          <SaveProfileButton
            type="button"
            onClick={handleSaveProfile}
            disabled={profileSaving || !editName.trim()}
          >
            {profileSaving ? '저장 중…' : '이름·등록번호 저장'}
          </SaveProfileButton>
          {profileFlash ? (
            <ProfileMessageText $error={profileFlash.type === 'err'}>{profileFlash.text}</ProfileMessageText>
          ) : null}
        </ProfileSaveBar>
      </InfoGrid>

      {/* 탭 섹션 */}
      <CustomerDetailTabs
        userIdx={parseInt(userIdx || '0')}
        onPointUpdate={loadCustomerDetail}
      />
    </MainContent>
  );
};

export default CustomerDetailPage; 